import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { UsageReport } from '@cortex/shared';
import { Api } from '../../lib/api';

const PALETTE = ['#5b9cf8', '#f59e0b', '#10b981', '#8b7cf6', '#ef4444', '#2e75b6', '#f97316', '#8890aa'];
const money = (c: number) => `$${(c / 100).toFixed(2)}`;
const k = (n: number) => (n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1)}M` : n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n));

/** Usage and cost per user per month, with a monthly stacked bar chart and a daily cost line. Sessions are attributed to the month/day they started (Brisbane). */
export function UsageTab() {
  const [months, setMonths] = useState(6);
  const q = useQuery({ queryKey: ['usage', months], queryFn: () => Api.agent.usage({ months, days: 60 }), staleTime: 30_000 });
  const r = q.data;
  const users = useMemo(() => {
    const m = new Map<string, { name: string; email: string }>();
    for (const mo of r?.months ?? []) for (const u of mo.byUser) m.set(u.userId, { name: u.name, email: u.email });
    return [...m.entries()].map(([id, u], i) => ({ id, ...u, colour: PALETTE[i % PALETTE.length] }));
  }, [r]);
  const colourOf = (id: string) => users.find((u) => u.id === id)?.colour ?? PALETTE[7];
  const [sel, setSel] = useState<string | null>(null);
  const month = r?.months.find((m) => m.month === sel) ?? r?.months[r.months.length - 1];

  if (q.isLoading) return <div className="muted">Loading usage…</div>;
  if (!r) return <div className="muted">Usage unavailable{q.error ? `: ${(q.error as Error).message}` : ''}.</div>;

  return (
    <div className="usage">
      <div className="admin-head">
        <span className="muted" style={{ fontSize: 12 }}>
          Cloud model spend per user, from the cost recorded on every agent session (provider list prices). Attributed to the month and day a session started, Brisbane time.
          {r.budget && r.budget.budgetCents > 0 && <> Cap this month: <b>{money(r.budget.budgetCents)}</b>, spent <b>{money(r.budget.spentCents)}</b>{r.budget.exhausted ? ', cap reached' : ''}.</>}
        </span>
        <span className="spacer" />
        <label className="row muted" style={{ fontSize: 12 }}>History
          <select value={months} onChange={(e) => setMonths(Number(e.target.value))}>{[3, 6, 12, 24].map((n) => <option key={n} value={n}>{n} months</option>)}</select>
        </label>
      </div>

      <div className="usage-grid">
        <section className="usage-card">
          <h3>Monthly cost by user</h3>
          <MonthlyBars report={r} colourOf={colourOf} selected={month?.month ?? null} onSelect={setSel} />
          <div className="usage-legend">{users.map((u) => <span key={u.id}><i style={{ background: u.colour }} /> {u.name}</span>)}</div>
        </section>
        <section className="usage-card">
          <h3>Daily cost, last 60 days</h3>
          <DailyBars report={r} />
        </section>
      </div>

      {month && (
        <section className="usage-card">
          <h3>{month.month} · {money(month.total.costCents)} · {month.total.sessions} sessions · {k(month.total.tokensIn)} in / {k(month.total.tokensOut)} out · {month.total.toolCalls} tool calls</h3>
          <table className="admin-table">
            <thead><tr><th>User</th><th>Cost</th><th>Share</th><th>Sessions</th><th>Tokens in</th><th>Tokens out</th><th>Tool calls</th><th>Cloud turns</th><th>Local turns</th></tr></thead>
            <tbody>
              {month.byUser.map((u) => (
                <tr key={u.userId}>
                  <td><i className="usage-dot" style={{ background: colourOf(u.userId) }} /> <b>{u.name}</b> <span className="faint">{u.email}</span></td>
                  <td className="mono">{money(u.costCents)}</td>
                  <td className="mono">{month.total.costCents ? `${Math.round((u.costCents / month.total.costCents) * 100)}%` : '–'}</td>
                  <td className="mono">{u.sessions}</td>
                  <td className="mono">{u.tokensIn.toLocaleString()}</td>
                  <td className="mono">{u.tokensOut.toLocaleString()}</td>
                  <td className="mono">{u.toolCalls}</td>
                  <td className="mono">{u.cloudTurns}</td>
                  <td className="mono">{u.localTurns}</td>
                </tr>
              ))}
              {!month.byUser.length && <tr><td colSpan={9} className="muted">No agent sessions this month.</td></tr>}
            </tbody>
          </table>
        </section>
      )}

      <section className="usage-card">
        <h3>Month by month</h3>
        <table className="admin-table">
          <thead><tr><th>Month</th><th>Cost</th><th>Sessions</th><th>Tokens in</th><th>Tokens out</th><th>Tool calls</th><th>Users</th></tr></thead>
          <tbody>
            {[...r.months].reverse().map((m) => (
              <tr key={m.month} className={m.month === month?.month ? 'on' : ''} onClick={() => setSel(m.month)} style={{ cursor: 'pointer' }}>
                <td className="mono">{m.month}</td><td className="mono">{money(m.total.costCents)}</td><td className="mono">{m.total.sessions}</td>
                <td className="mono">{m.total.tokensIn.toLocaleString()}</td><td className="mono">{m.total.tokensOut.toLocaleString()}</td><td className="mono">{m.total.toolCalls}</td><td className="mono">{m.byUser.length}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}

function MonthlyBars({ report, colourOf, selected, onSelect }: { report: UsageReport; colourOf: (id: string) => string; selected: string | null; onSelect: (m: string) => void }) {
  const W = 640, H = 200, padL = 44, padB = 22, padT = 8;
  const max = Math.max(1, ...report.months.map((m) => m.total.costCents));
  const bw = (W - padL) / Math.max(1, report.months.length);
  const y = (c: number) => padT + (H - padT - padB) * (1 - c / max);
  const ticks = [0, 0.5, 1].map((f) => Math.round(max * f));
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="usage-chart" role="img" aria-label="Monthly cost by user">
      {ticks.map((t) => <g key={t}><line x1={padL} x2={W} y1={y(t)} y2={y(t)} className="grid" /><text x={padL - 6} y={y(t) + 4} textAnchor="end" className="tick">{money(t)}</text></g>)}
      {report.months.map((m, i) => {
        let acc = 0;
        const x = padL + i * bw + bw * 0.15;
        return (
          <g key={m.month} onClick={() => onSelect(m.month)} style={{ cursor: 'pointer' }} opacity={selected && selected !== m.month ? 0.55 : 1}>
            {m.byUser.map((u) => {
              const y0 = y(acc + u.costCents), h = y(acc) - y0; acc += u.costCents;
              return <rect key={u.userId} x={x} y={y0} width={bw * 0.7} height={Math.max(0, h)} fill={colourOf(u.userId)}><title>{`${m.month} · ${u.name} · ${money(u.costCents)}`}</title></rect>;
            })}
            <text x={x + bw * 0.35} y={H - 6} textAnchor="middle" className="tick">{m.month.slice(2)}</text>
            {m.total.costCents > 0 && <text x={x + bw * 0.35} y={y(m.total.costCents) - 4} textAnchor="middle" className="tick strong">{money(m.total.costCents)}</text>}
          </g>
        );
      })}
    </svg>
  );
}

function DailyBars({ report }: { report: UsageReport }) {
  const W = 640, H = 200, padL = 44, padB = 22, padT = 8;
  const days = report.daily;
  const max = Math.max(1, ...days.map((d) => d.costCents));
  const bw = (W - padL) / Math.max(1, days.length);
  const y = (c: number) => padT + (H - padT - padB) * (1 - c / max);
  const ticks = [0, 0.5, 1].map((f) => Math.round(max * f));
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="usage-chart" role="img" aria-label="Daily cost">
      {ticks.map((t) => <g key={t}><line x1={padL} x2={W} y1={y(t)} y2={y(t)} className="grid" /><text x={padL - 6} y={y(t) + 4} textAnchor="end" className="tick">{money(t)}</text></g>)}
      {days.map((d, i) => (
        <rect key={d.date} x={padL + i * bw + bw * 0.1} y={y(d.costCents)} width={Math.max(1, bw * 0.8)} height={Math.max(0, y(0) - y(d.costCents))} fill={d.costCents ? '#5b9cf8' : 'transparent'}>
          <title>{`${d.date} · ${money(d.costCents)} · ${d.sessions} sessions`}</title>
        </rect>
      ))}
      {days.filter((_, i) => i % 10 === 0 || i === days.length - 1).map((d) => {
        const i = days.indexOf(d);
        return <text key={d.date} x={padL + i * bw + bw / 2} y={H - 6} textAnchor="middle" className="tick">{d.date.slice(5)}</text>;
      })}
    </svg>
  );
}
